import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Minus, Plus, X, Shield, Truck, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAccess } from '../context/AccessContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, clearCart, total, recordOrder } = useCart();
  const { memberEmail } = useAccess();
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    zip: '',
    country: ''
  });

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleCheckout = async () => {
    if (items.length === 0) return;
    
    setIsProcessing(true);

    try {
      const orderData = {
        email: memberEmail,
        items: items.map(item => ({
          product_id: item.id,
          product_name: `${item.name}${item.selectedSize ? ` (${item.selectedSize})` : ''}`,
          quantity: item.quantity,
          price: item.price
        })),
        shipping_address: `${formData.name}, ${formData.address}, ${formData.city}, ${formData.zip}, ${formData.country}`
      };

      const response = await axios.post(`${API}/orders`, orderData);
      
      // Hidden: Record order for gift system
      recordOrder(total);
      
      clearCart();
      navigate('/confirmation', { state: { orderId: response.data.id } });
    } catch (error) {
      console.error('Checkout error:', error);
      // Mock success for demo
      recordOrder(total);
      clearCart();
      navigate('/confirmation', { state: { orderId: 'S13-' + Date.now().toString(36).toUpperCase() } });
    } finally {
      setIsProcessing(false);
    }
  };

  // Get cart key for item
  const getCartKey = (item) => item.cartKey || item.id;

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-zinc-50 mb-4">Your Collection is Empty</h1>
          <p className="font-sans text-sm text-zinc-500 mb-8">
            Explore our secrets to find your perfect experience.
          </p>
          <button
            data-testid="continue-shopping"
            onClick={() => navigate('/collection')}
            className="bg-white text-black px-8 py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300"
          >
            Explore Collection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-6 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-sans text-xs tracking-widest uppercase">Back</span>
          </button>
          <h1 
            onClick={() => navigate('/collection')}
            className="font-serif text-xl text-zinc-50 cursor-pointer"
          >
            Secret 13
          </h1>
          <div className="w-20" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-serif text-3xl sm:text-4xl text-zinc-50 mb-4 text-center"
        >
          Your Collection
        </motion.h1>
        
        {/* Psychology Line */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="font-sans text-xs text-zinc-600 text-center mb-12 tracking-wide"
        >
          Each order is carefully prepared.
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Collection Summary */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6">
              Selected Secrets
            </h2>

            <div className="space-y-6">
              {items.map((item) => (
                <div 
                  key={getCartKey(item)}
                  data-testid={`collection-item-${item.id}`}
                  className="flex gap-6 pb-6 border-b border-white/5"
                >
                  {/* Image */}
                  <div className="w-20 h-28 bg-[#0A0A0A] border border-white/5 overflow-hidden flex-shrink-0">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <h3 className="font-serif text-lg text-zinc-50">{item.name}</h3>
                          <p className="font-sans text-xs text-zinc-500">
                            {item.secretName}
                            {item.selectedSize && <span className="ml-2">• {item.selectedSize}</span>}
                          </p>
                        </div>
                        <button
                          data-testid={`remove-${item.id}`}
                          onClick={() => removeItem(getCartKey(item))}
                          className="text-zinc-700 hover:text-zinc-400 transition-colors p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      {/* Quantity */}
                      <div className="flex items-center border border-zinc-800">
                        <button
                          data-testid={`decrease-${item.id}`}
                          onClick={() => updateQuantity(getCartKey(item), item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white transition-colors"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 h-8 flex items-center justify-center text-sm text-white">
                          {item.quantity}
                        </span>
                        <button
                          data-testid={`increase-${item.id}`}
                          onClick={() => updateQuantity(getCartKey(item), item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Price */}
                      <p className="font-sans text-white">${item.price * item.quantity}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="mt-8 pt-6 border-t border-white/5">
              <div className="flex items-center justify-between mb-3">
                <span className="font-sans text-sm text-zinc-500">Subtotal</span>
                <span className="font-sans text-white">${total}</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="font-sans text-sm text-zinc-500">Shipping</span>
                <span className="font-sans text-xs text-zinc-600">Complimentary</span>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-white/5">
                <span className="font-serif text-lg text-zinc-50">Total</span>
                <span 
                  data-testid="checkout-total"
                  className="font-serif text-2xl text-zinc-50"
                >
                  ${total}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Shipping & Payment Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            {/* Shipping */}
            <h2 className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6">
              Shipping
            </h2>

            <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); handleCheckout(); }}>
              <input
                type="text"
                name="name"
                data-testid="shipping-name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Full Name"
                required
                className="w-full bg-transparent border-b border-zinc-800 focus:border-white py-3 text-white font-sans text-sm placeholder:text-zinc-600 transition-colors outline-none"
              />

              <input
                type="text"
                name="address"
                data-testid="shipping-address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Street Address"
                required
                className="w-full bg-transparent border-b border-zinc-800 focus:border-white py-3 text-white font-sans text-sm placeholder:text-zinc-600 transition-colors outline-none"
              />

              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  name="city"
                  data-testid="shipping-city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City"
                  required
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-white py-3 text-white font-sans text-sm placeholder:text-zinc-600 transition-colors outline-none"
                />
                <input
                  type="text"
                  name="zip"
                  data-testid="shipping-zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  placeholder="ZIP"
                  required
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-white py-3 text-white font-sans text-sm placeholder:text-zinc-600 transition-colors outline-none"
                />
              </div>

              <input
                type="text"
                name="country"
                data-testid="shipping-country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Country"
                required
                className="w-full bg-transparent border-b border-zinc-800 focus:border-white py-3 text-white font-sans text-sm placeholder:text-zinc-600 transition-colors outline-none"
              />

              {/* Payment */}
              <div className="pt-8">
                <h3 className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6">
                  Payment
                </h3>
                <div className="bg-zinc-900/30 border border-white/5 p-5 text-center">
                  <p className="font-sans text-zinc-500 text-xs">
                    Demo mode — no payment required
                  </p>
                </div>
              </div>

              {/* Complete Collection Button */}
              <button
                type="submit"
                data-testid="complete-order-button"
                disabled={isProcessing}
                className="w-full bg-white text-black py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mt-6"
              >
                {isProcessing ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  'Complete Collection'
                )}
              </button>
            </form>

            {/* Trust Badges */}
            <div className="mt-8 pt-6 border-t border-white/5">
              <div className="flex flex-wrap gap-6 justify-center">
                <div className="flex items-center gap-2 text-zinc-600">
                  <Shield className="w-4 h-4" />
                  <span className="font-sans text-[10px] tracking-wide">Secure</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-600">
                  <Truck className="w-4 h-4" />
                  <span className="font-sans text-[10px] tracking-wide">Discreet</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-600">
                  <MapPin className="w-4 h-4" />
                  <span className="font-sans text-[10px] tracking-wide">Tracked</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
